// src/components/common/UpdateNotification.js
import React, { useState, useEffect } from 'react';
import { Button, Snackbar, Alert, Box, Typography, LinearProgress, Chip } from '@mui/material';
import { SystemUpdateAlt, NewReleases, CheckCircle } from '@mui/icons-material';
import { checkVersionAndClearCache } from '../../services/versionManager';

/**
 * Enhanced update notification component
 * Shows a notification when a new version is available
 * Less intrusive with background updates and smoother reload
 */
const UpdateNotification = () => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateAccepted, setUpdateAccepted] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [dismissUntil, setDismissUntil] = useState(null);
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    // Check if a service worker update is waiting
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Listen for the controlling service worker changing with smooth transition
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        
        // Smooth fade before reload
        document.body.style.opacity = '0';
        document.body.style.transition = 'opacity 0.3s ease-out';
        
        setTimeout(() => {
          window.location.reload();
        }, 300);
      });

      // Check for updates every 1 hour (less intrusive than 30min)
      const checkForUpdates = async () => {
        // Skip if user dismissed until a future time
        if (dismissUntil && Date.now() < dismissUntil) {
          return;
        }

        try {
          const registration = await navigator.serviceWorker.ready;
          
          // Background update check first
          await registration.update();
          
          if (registration.waiting) {
            // Check version to show upgrade info
            const versionCheckResult = await checkVersionAndClearCache();
            
            setUpdateInfo({ 
              type: 'service_worker', 
              message: 'A new version is available!',
              fromVersion: versionCheckResult.fromVersion,
              toVersion: versionCheckResult.toVersion || 'latest'
            });
            setCountdown(10); // Reset countdown
          }
          
          // Check if app version was changed on server
          const versionCheckResult = await checkVersionAndClearCache();
          if (versionCheckResult.updated) {
            setUpdateInfo({
              type: 'version_update',
              message: `Successfully updated to version ${versionCheckResult.toVersion}!`,
              toVersion: versionCheckResult.toVersion,
              fromVersion: versionCheckResult.fromVersion,
              isFirstRun: versionCheckResult.isFirstRun
            });
            setCountdown(6); // Shorter for success message
          }
        } catch (error) {
          console.error('Failed to check for updates:', error);
        }
      };

      // Check immediately and then every 1 hour
      checkForUpdates();
      const interval = setInterval(checkForUpdates, 60 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [dismissUntil]);

  // Auto-dismiss countdown for success messages
  useEffect(() => {
    if (updateInfo?.type === 'version_update' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (updateInfo?.type === 'version_update' && countdown === 0) {
      handleDismiss();
    }
  }, [updateInfo, countdown]);

  const handleUpdate = () => {
    setIsUpdating(true);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          setUpdateAccepted(true);
          
          // The controllerchange listener will handle the smooth reload
        } else {
          // Smooth fade before reload
          document.body.style.opacity = '0';
          document.body.style.transition = 'opacity 0.3s ease-out';
          
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }
      });
    } else {
      // Smooth fade before reload
      document.body.style.opacity = '0';
      document.body.style.transition = 'opacity 0.3s ease-out';
      
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  };

  const handleUpdateLater = () => {
    // Dismiss for 1 hour
    setDismissUntil(Date.now() + 60 * 60 * 1000);
    setUpdateInfo(null);
  };

  const handleDismiss = () => {
    setUpdateInfo(null);
    setCountdown(10);
  };

  const toggleChangelog = () => {
    setShowChangelog(!showChangelog);
  };

  if (!updateInfo || updateAccepted) {
    return null;
  }

  // Version update (success message) - auto-dismissible
  if (updateInfo.type === 'version_update') {
    return (
      <Snackbar 
        open={true}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={countdown * 1000}
        onClose={handleDismiss}
        sx={{
          '& .MuiSnackbarContent-root': {
            minWidth: 'auto',
          },
        }}
      >
        <Alert 
          severity="success"
          variant="filled"
          onClose={handleDismiss}
          icon={<CheckCircle />}
          sx={{
            alignItems: 'center',
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {updateInfo.message}
            {updateInfo.toVersion && (
              <Chip 
                label={`v${updateInfo.toVersion}`} 
                size="small" 
                color="success"
                variant="outlined"
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </Alert>
      </Snackbar>
    );
  }

  // Service worker update (action required) - less intrusive, bottom-right
  return (
    <Snackbar 
      open={true}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{
        '& .MuiSnackbarContent-root': {
          minWidth: 'auto',
        },
      }}
    >
      <Alert 
        severity="info"
        variant="filled"
        icon={<NewReleases />}
        onClose={handleDismiss}
        sx={{
          maxWidth: { xs: 'calc(100vw - 32px)', sm: '400px' },
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {updateInfo.message}
            </Typography>
          </Box>
          
          {updateInfo.fromVersion && updateInfo.toVersion && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
              <Chip 
                label={`v${updateInfo.fromVersion}`} 
                size="small" 
                variant="outlined"
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: 'rgba(255,255,255,0.7)',
                }}
              />
              <SystemUpdateAlt sx={{ fontSize: 16, opacity: 0.7 }} />
              <Chip 
                label={`v${updateInfo.toVersion}`} 
                size="small" 
                color="primary"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              />
            </Box>
          )}
          
          {showChangelog && (
            <Box 
              sx={{ 
                mt: 1.5, 
                mb: 1.5, 
                p: 1.5, 
                bgcolor: 'rgba(0,0,0,0.2)', 
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                What's new:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, lineHeight: 1.4 }}>
                • Performance improvements{'\n'}
                • Enhanced offline support{'\n'}
                • Bug fixes and stability
              </Typography>
            </Box>
          )}
          
          {isUpdating && (
            <Box sx={{ mt: 1.5, mb: 1 }}>
              <LinearProgress 
                sx={{ 
                  borderRadius: 1,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  },
                }}
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                Updating...
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleUpdateLater}
              disabled={isUpdating}
              sx={{ 
                flex: 1,
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)',
                },
              }}
            >
              Later
            </Button>
            <Button 
              color="inherit" 
              size="small" 
              variant="contained"
              onClick={handleUpdate}
              disabled={isUpdating}
              sx={{ 
                flex: 1,
                bgcolor: 'rgba(255,255,255,0.9)',
                color: 'primary.main',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'white',
                },
              }}
            >
              Update Now
            </Button>
          </Box>
          
          {!showChangelog && (
            <Button
              color="inherit"
              size="small"
              onClick={toggleChangelog}
              sx={{ 
                mt: 1,
                width: '100%',
                fontSize: '0.7rem',
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              View changelog
            </Button>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default UpdateNotification;
